import React, { useState, useEffect, useRef } from 'react';
import { 
  X, ArrowUpRight, Circle, Square, Type, RotateCw, 
  Trash2, Save, RefreshCw, MousePointer, Edit3, Lock
} from 'lucide-react';
import { toast } from 'sonner';

const SimpleImageEditor = ({ image, imageIndex, totalImages, onClose, onNext, onPrevious, onSave }) => {
  const canvasRef = useRef(null);
  const [ctx, setCtx] = useState(null);
  const [backgroundImage, setBackgroundImage] = useState(null);
  const [objects, setObjects] = useState([]);
  const [selectedObject, setSelectedObject] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [isRotating, setIsRotating] = useState(false);
  const [resizeHandle, setResizeHandle] = useState(null);
  const [dragStart, setDragStart] = useState(null);
  const [activeTool, setActiveTool] = useState('select');
  const [color, setColor] = useState('#FF0000');
  const [textValue, setText] = useState('');

  const colors = ['#000000', '#808080', '#00FF00', '#FF0000', '#0000FF', '#FF00FF', '#FFFF00', '#FFFFFF'];

  // Canvas initialisieren
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    canvas.width = 900;
    canvas.height = 650;
    const context = canvas.getContext('2d');
    setCtx(context);

    // Objekte zurücksetzen beim Bildwechsel
    setObjects([]);
    setSelectedObject(null);

    // Bild laden
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const scale = Math.min(canvas.width / img.width, canvas.height / img.height) * 0.85;
      const x = (canvas.width - img.width * scale) / 2;
      const y = (canvas.height - img.height * scale) / 2;
      
      setBackgroundImage({
        img,
        x,
        y,
        width: img.width * scale,
        height: img.height * scale
      });
    };
    img.src = `${process.env.REACT_APP_BACKEND_URL}${image.url}`;
  }, [image]);

  // Canvas zeichnen
  useEffect(() => {
    if (!ctx || !backgroundImage) return;

    const canvas = canvasRef.current;
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Hintergrund
    ctx.fillStyle = '#f3f4f6';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Bild
    ctx.drawImage(
      backgroundImage.img,
      backgroundImage.x,
      backgroundImage.y,
      backgroundImage.width,
      backgroundImage.height
    );

    // Objekte zeichnen
    objects.forEach((obj, index) => {
      ctx.save();
      
      // Rotation anwenden
      const rotation = obj.rotation || 0;
      if (rotation !== 0) {
        const bounds = getObjectBounds(obj);
        const centerX = bounds.x + bounds.width / 2;
        const centerY = bounds.y + bounds.height / 2;
        ctx.translate(centerX, centerY);
        ctx.rotate(rotation);
        ctx.translate(-centerX, -centerY);
      }
      
      if (obj.type === 'circle') {
        ctx.beginPath();
        ctx.arc(obj.x, obj.y, obj.radius, 0, Math.PI * 2);
        ctx.strokeStyle = obj.color;
        ctx.lineWidth = 3;
        ctx.stroke();
        if (obj.filled) {
          ctx.fillStyle = obj.color;
          ctx.fill();
        }
      } else if (obj.type === 'rect') {
        ctx.strokeStyle = obj.color;
        ctx.lineWidth = 3;
        ctx.strokeRect(obj.x, obj.y, obj.width, obj.height);
        if (obj.filled) {
          ctx.fillStyle = obj.color;
          ctx.fillRect(obj.x, obj.y, obj.width, obj.height);
        }
      } else if (obj.type === 'arrow') {
        const arrowWidth = obj.width || 100;
        const arrowHeight = obj.height || 100;
        drawArrow(ctx, obj.x, obj.y, obj.x + arrowWidth, obj.y + arrowHeight, obj.color);
      } else if (obj.type === 'text') {
        ctx.font = '24px Arial';
        ctx.fillStyle = obj.color;
        ctx.fillText(obj.text, obj.x, obj.y);
      }

      // Handles für ausgewähltes Objekt
      if (selectedObject === index) {
        ctx.strokeStyle = '#0000FF';
        ctx.lineWidth = 2;
        ctx.setLineDash([5, 5]);
        
        let bounds = {};
        if (obj.type === 'circle') {
          bounds = {
            x: obj.x - obj.radius,
            y: obj.y - obj.radius,
            width: obj.radius * 2,
            height: obj.radius * 2
          };
          ctx.strokeRect(bounds.x - 5, bounds.y - 5, bounds.width + 10, bounds.height + 10);
        } else if (obj.type === 'rect') {
          bounds = { x: obj.x, y: obj.y, width: obj.width, height: obj.height };
          ctx.strokeRect(bounds.x - 5, bounds.y - 5, bounds.width + 10, bounds.height + 10);
        } else if (obj.type === 'arrow' || obj.type === 'text') {
          bounds = { x: obj.x, y: obj.y, width: 100, height: 100 };
          ctx.strokeRect(bounds.x - 5, bounds.y - 5, bounds.width + 10, bounds.height + 10);
        }
        
        ctx.setLineDash([]);
        
        // Resize-Handles (kleine Quadrate an den Ecken)
        const handleSize = 8;
        ctx.fillStyle = '#0000FF';
        
        // Oben links
        ctx.fillRect(bounds.x - handleSize/2, bounds.y - handleSize/2, handleSize, handleSize);
        // Oben rechts
        ctx.fillRect(bounds.x + bounds.width - handleSize/2, bounds.y - handleSize/2, handleSize, handleSize);
        // Unten links
        ctx.fillRect(bounds.x - handleSize/2, bounds.y + bounds.height - handleSize/2, handleSize, handleSize);
        // Unten rechts
        ctx.fillRect(bounds.x + bounds.width - handleSize/2, bounds.y + bounds.height - handleSize/2, handleSize, handleSize);
        
        // Rotations-Handle (grüner Kreis oben mittig)
        ctx.fillStyle = '#00FF00';
        ctx.beginPath();
        ctx.arc(bounds.x + bounds.width / 2, bounds.y - 20, 6, 0, Math.PI * 2);
        ctx.fill();
        
        // Linie zum Rotations-Handle
        ctx.strokeStyle = '#00FF00';
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(bounds.x + bounds.width / 2, bounds.y);
        ctx.lineTo(bounds.x + bounds.width / 2, bounds.y - 20);
        ctx.stroke();
        
        // Delete-Button (roter Kreis oben rechts)
        ctx.fillStyle = '#FF0000';
        ctx.beginPath();
        ctx.arc(bounds.x + bounds.width + 10, bounds.y - 10, 8, 0, Math.PI * 2);
        ctx.fill();
        
        // X im Delete-Button
        ctx.strokeStyle = '#FFFFFF';
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(bounds.x + bounds.width + 5, bounds.y - 15);
        ctx.lineTo(bounds.x + bounds.width + 15, bounds.y - 5);
        ctx.moveTo(bounds.x + bounds.width + 15, bounds.y - 15);
        ctx.lineTo(bounds.x + bounds.width + 5, bounds.y - 5);
        ctx.stroke();
      }

      ctx.restore();
    });
  }, [ctx, backgroundImage, objects, selectedObject]);

  // Pfeil zeichnen
  const drawArrow = (ctx, fromX, fromY, toX, toY, color) => {
    const headlen = 15;
    const angle = Math.atan2(toY - fromY, toX - fromX);

    ctx.strokeStyle = color;
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.moveTo(fromX, fromY);
    ctx.lineTo(toX, toY);
    ctx.stroke();

    ctx.beginPath();
    ctx.moveTo(toX, toY);
    ctx.lineTo(toX - headlen * Math.cos(angle - Math.PI / 6), toY - headlen * Math.sin(angle - Math.PI / 6));
    ctx.lineTo(toX - headlen * Math.cos(angle + Math.PI / 6), toY - headlen * Math.sin(angle + Math.PI / 6));
    ctx.closePath();
    ctx.fillStyle = color;
    ctx.fill();
  };

  // Hilfsfunktion: Bounds eines Objekts berechnen
  const getObjectBounds = (obj) => {
    if (obj.type === 'circle') {
      return {
        x: obj.x - obj.radius,
        y: obj.y - obj.radius,
        width: obj.radius * 2,
        height: obj.radius * 2
      };
    } else if (obj.type === 'rect') {
      return { x: obj.x, y: obj.y, width: obj.width, height: obj.height };
    } else if (obj.type === 'arrow') {
      return { 
        x: obj.x, 
        y: obj.y, 
        width: obj.width || 100, 
        height: obj.height || 100 
      };
    } else if (obj.type === 'text') {
      return { x: obj.x, y: obj.y, width: 100, height: 30 };
    }
    return null;
  };

  // Hilfsfunktion: Prüfe ob Maus auf Resize-Handle ist
  const getResizeHandle = (x, y, bounds) => {
    const handleSize = 8;
    const tolerance = 5;
    
    // Oben links
    if (Math.abs(x - bounds.x) < tolerance && Math.abs(y - bounds.y) < tolerance) {
      return 'nw';
    }
    // Oben rechts
    if (Math.abs(x - (bounds.x + bounds.width)) < tolerance && Math.abs(y - bounds.y) < tolerance) {
      return 'ne';
    }
    // Unten links
    if (Math.abs(x - bounds.x) < tolerance && Math.abs(y - (bounds.y + bounds.height)) < tolerance) {
      return 'sw';
    }
    // Unten rechts
    if (Math.abs(x - (bounds.x + bounds.width)) < tolerance && Math.abs(y - (bounds.y + bounds.height)) < tolerance) {
      return 'se';
    }
    return null;
  };

  // Hilfsfunktion: Prüfe ob Maus auf Rotations-Handle ist
  const isOnRotationHandle = (x, y, bounds) => {
    const handleX = bounds.x + bounds.width / 2;
    const handleY = bounds.y - 20;
    const dist = Math.sqrt((x - handleX) ** 2 + (y - handleY) ** 2);
    return dist <= 10;
  };

  // Hilfsfunktion: Prüfe ob Maus auf Delete-Button ist
  const isOnDeleteButton = (x, y, bounds) => {
    const buttonX = bounds.x + bounds.width + 10;
    const buttonY = bounds.y - 10;
    const dist = Math.sqrt((x - buttonX) ** 2 + (y - buttonY) ** 2);
    return dist <= 10;
  };

  // Maus-Events
  const handleMouseDown = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    // Prüfe zuerst, ob ein Handle angeklickt wurde
    if (selectedObject !== null) {
      const obj = objects[selectedObject];
      const bounds = getObjectBounds(obj);
      
      // Delete-Button prüfen
      if (isOnDeleteButton(x, y, bounds)) {
        deleteSelected();
        return;
      }
      
      // Rotations-Handle prüfen
      if (isOnRotationHandle(x, y, bounds)) {
        setIsRotating(true);
        const centerX = bounds.x + bounds.width / 2;
        const centerY = bounds.y + bounds.height / 2;
        const startAngle = Math.atan2(y - centerY, x - centerX);
        setDragStart({ 
          x, 
          y, 
          obj: { ...obj },
          bounds: { ...bounds },
          centerX,
          centerY,
          startAngle,
          startRotation: obj.rotation || 0
        });
        return;
      }
      
      // Resize-Handle prüfen
      const handle = getResizeHandle(x, y, bounds);
      if (handle) {
        setIsResizing(true);
        setResizeHandle(handle);
        setDragStart({ 
          x, 
          y, 
          obj: { ...obj },
          bounds: { ...bounds }
        });
        return;
      }
    }

    // Objekt suchen
    for (let i = objects.length - 1; i >= 0; i--) {
      const obj = objects[i];
      let hit = false;

      if (obj.type === 'circle') {
        const dist = Math.sqrt((x - obj.x) ** 2 + (y - obj.y) ** 2);
        hit = dist <= obj.radius;
      } else if (obj.type === 'rect') {
        hit = x >= obj.x && x <= obj.x + obj.width && y >= obj.y && y <= obj.y + obj.height;
      } else if (obj.type === 'arrow') {
        const arrowWidth = obj.width || 100;
        const arrowHeight = obj.height || 100;
        hit = x >= obj.x && x <= obj.x + arrowWidth && y >= obj.y && y <= obj.y + arrowHeight;
      } else if (obj.type === 'text') {
        hit = x >= obj.x && x <= obj.x + 100 && y >= obj.y - 20 && y <= obj.y + 10;
      }

      if (hit) {
        setSelectedObject(i);
        setIsDragging(true);
        setDragStart({ x, y, objX: obj.x, objY: obj.y });
        return;
      }
    }

    setSelectedObject(null);
  };

  const handleMouseMove = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    // Cursor ändern bei Handles
    if (!isDragging && !isResizing && !isRotating && selectedObject !== null) {
      const obj = objects[selectedObject];
      const bounds = getObjectBounds(obj);
      
      // Delete-Button
      if (isOnDeleteButton(x, y, bounds)) {
        canvas.style.cursor = 'pointer';
      }
      // Rotations-Handle
      else if (isOnRotationHandle(x, y, bounds)) {
        canvas.style.cursor = 'grab';
      } else {
        // Resize-Handles
        const handle = getResizeHandle(x, y, bounds);
        if (handle === 'nw' || handle === 'se') {
          canvas.style.cursor = 'nwse-resize';
        } else if (handle === 'ne' || handle === 'sw') {
          canvas.style.cursor = 'nesw-resize';
        } else {
          canvas.style.cursor = 'move';
        }
      }
    } else if (isRotating) {
      canvas.style.cursor = 'grabbing';
    } else {
      canvas.style.cursor = 'default';
    }

    // Rotating
    if (isRotating && selectedObject !== null && dragStart) {
      const centerX = dragStart.centerX;
      const centerY = dragStart.centerY;
      const currentAngle = Math.atan2(y - centerY, x - centerX);
      const angleDiff = currentAngle - dragStart.startAngle;
      
      setObjects(prev => {
        const newObjects = [...prev];
        newObjects[selectedObject] = {
          ...newObjects[selectedObject],
          rotation: dragStart.startRotation + angleDiff
        };
        return newObjects;
      });
      return;
    }

    // Resizing
    if (isResizing && selectedObject !== null && dragStart) {
      const dx = x - dragStart.x;
      const dy = y - dragStart.y;
      const obj = dragStart.obj;
      const bounds = dragStart.bounds;

      setObjects(prev => {
        const newObjects = [...prev];
        const currentObj = { ...newObjects[selectedObject] };

        if (currentObj.type === 'circle') {
          // Kreis: Radius ändern
          const newRadius = Math.max(20, obj.radius + (dx + dy) / 2);
          currentObj.radius = newRadius;
          
          // Position anpassen je nach Handle
          if (resizeHandle === 'nw') {
            currentObj.x = bounds.x + bounds.width / 2;
            currentObj.y = bounds.y + bounds.height / 2;
          } else if (resizeHandle === 'ne') {
            currentObj.x = bounds.x + bounds.width / 2;
            currentObj.y = bounds.y + bounds.height / 2;
          } else if (resizeHandle === 'sw') {
            currentObj.x = bounds.x + bounds.width / 2;
            currentObj.y = bounds.y + bounds.height / 2;
          } else if (resizeHandle === 'se') {
            currentObj.x = bounds.x + bounds.width / 2;
            currentObj.y = bounds.y + bounds.height / 2;
          }
        } else if (currentObj.type === 'rect' || currentObj.type === 'arrow') {
          // Rechteck & Pfeil: Width/Height ändern
          if (resizeHandle === 'se') {
            currentObj.width = Math.max(20, obj.width + dx);
            currentObj.height = Math.max(20, obj.height + dy);
          } else if (resizeHandle === 'sw') {
            currentObj.x = obj.x + dx;
            currentObj.width = Math.max(20, obj.width - dx);
            currentObj.height = Math.max(20, obj.height + dy);
          } else if (resizeHandle === 'ne') {
            currentObj.y = obj.y + dy;
            currentObj.width = Math.max(20, obj.width + dx);
            currentObj.height = Math.max(20, obj.height - dy);
          } else if (resizeHandle === 'nw') {
            currentObj.x = obj.x + dx;
            currentObj.y = obj.y + dy;
            currentObj.width = Math.max(20, obj.width - dx);
            currentObj.height = Math.max(20, obj.height - dy);
          }
        }

        newObjects[selectedObject] = currentObj;
        return newObjects;
      });
      return;
    }

    // Dragging
    if (isDragging && selectedObject !== null && dragStart) {
      const dx = x - dragStart.x;
      const dy = y - dragStart.y;

      setObjects(prev => {
        const newObjects = [...prev];
        newObjects[selectedObject] = {
          ...newObjects[selectedObject],
          x: dragStart.objX + dx,
          y: dragStart.objY + dy
        };
        return newObjects;
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    setIsResizing(false);
    setIsRotating(false);
    setResizeHandle(null);
    setDragStart(null);
  };

  // Werkzeuge
  const addCircle = (filled) => {
    setObjects(prev => [...prev, {
      type: 'circle',
      x: 200,
      y: 200,
      radius: 50,
      color,
      filled
    }]);
    toast.success('Kreis hinzugefügt - Klicken & Ziehen zum Verschieben');
  };

  const addRectangle = (filled) => {
    setObjects(prev => [...prev, {
      type: 'rect',
      x: 200,
      y: 200,
      width: 100,
      height: 60,
      color,
      filled
    }]);
    toast.success('Rechteck hinzugefügt');
  };

  const addArrow = () => {
    setObjects(prev => [...prev, {
      type: 'arrow',
      x: 200,
      y: 200,
      width: 100,
      height: 100,
      color
    }]);
    toast.success('Pfeil hinzugefügt');
  };

  const addText = () => {
    setObjects(prev => [...prev, {
      type: 'text',
      x: 200,
      y: 200,
      text: textValue || 'Text',
      color
    }]);
    toast.success('Text hinzugefügt');
  };

  const addBlackBar = () => {
    setObjects(prev => [...prev, {
      type: 'rect',
      x: 200,
      y: 200,
      width: 150,
      height: 40,
      color: '#000000',
      filled: true
    }]);
    toast.success('Schwarzer Balken hinzugefügt');
  };

  const deleteSelected = () => {
    if (selectedObject !== null) {
      setObjects(prev => prev.filter((_, i) => i !== selectedObject));
      setSelectedObject(null);
      toast.success('Objekt gelöscht');
    } else {
      toast.error('Kein Objekt ausgewählt');
    }
  };

  // Keyboard-Support für Löschen
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Delete' || e.key === 'Backspace') {
        if (selectedObject !== null) {
          e.preventDefault();
          deleteSelected();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedObject]);

  const handleSave = async () => {
    try {
      const canvas = canvasRef.current;
      const dataURL = canvas.toDataURL('image/png', 0.95);
      
      const response = await fetch(dataURL);
      const blob = await response.blob();
      
      const formData = new FormData();
      const filename = image.url.split('/').pop();
      formData.append('file', blob, filename);
      
      const saveResponse = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/lichtbilder/save-edited/${filename}`,
        {
          method: 'POST',
          body: formData
        }
      );
      
      if (saveResponse.ok) {
        toast.success('Bild erfolgreich gespeichert!');
        if (onSave) onSave();
        onClose();
      }
    } catch (error) {
      toast.error('Speichern fehlgeschlagen');
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center p-2">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-6xl max-h-[96vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-3 py-2 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Edit3 className="w-5 h-5" />
            <h3 className="font-bold text-base">Bildbearbeitung (Einfach & Zuverlässig)</h3>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-white/20 rounded">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 flex overflow-hidden">
          {/* Werkzeuge */}
          <div className="w-56 bg-gray-50 border-r overflow-y-auto p-2 space-y-2">
            <h4 className="font-bold text-sm px-2">WERKZEUGE</h4>

            <button onClick={() => addCircle(false)} className="w-full flex items-center gap-2 px-2 py-1.5 rounded text-sm bg-white hover:bg-blue-50">
              <Circle className="w-4 h-4" />
              <span>Kreis</span>
            </button>

            <button onClick={() => addCircle(true)} className="w-full flex items-center gap-2 px-2 py-1.5 rounded text-sm bg-white hover:bg-blue-50">
              <Circle className="w-4 h-4 fill-current" />
              <span>Kreis gefüllt</span>
            </button>

            <button onClick={() => addRectangle(false)} className="w-full flex items-center gap-2 px-2 py-1.5 rounded text-sm bg-white hover:bg-blue-50">
              <Square className="w-4 h-4" />
              <span>Rechteck</span>
            </button>

            <button onClick={() => addRectangle(true)} className="w-full flex items-center gap-2 px-2 py-1.5 rounded text-sm bg-white hover:bg-blue-50">
              <Square className="w-4 h-4 fill-current" />
              <span>Rechteck gefüllt</span>
            </button>

            <button onClick={addArrow} className="w-full flex items-center gap-2 px-2 py-1.5 rounded text-sm bg-white hover:bg-blue-50">
              <ArrowUpRight className="w-4 h-4" />
              <span>Pfeil</span>
            </button>

            <div className="bg-white p-2 rounded shadow-sm space-y-1">
              <p className="text-xs font-semibold text-gray-600">Text</p>
              <input type="text" value={textValue} onChange={(e) => setText(e.target.value)} placeholder="Text..." className="w-full px-2 py-1 text-sm border rounded" />
              <button onClick={addText} className="w-full flex items-center gap-2 px-2 py-1 rounded text-sm bg-blue-500 text-white hover:bg-blue-600">
                <Type className="w-4 h-4" />
                <span>Hinzufügen</span>
              </button>
            </div>

            <button onClick={addBlackBar} className="w-full flex items-center gap-2 px-2 py-1.5 rounded text-sm bg-gray-800 text-white hover:bg-gray-900">
              <Lock className="w-4 h-4" />
              <span>Kennzeichen</span>
            </button>

            <button onClick={deleteSelected} className="w-full flex items-center gap-2 px-2 py-1.5 rounded text-sm hover:bg-red-50 hover:text-red-600">
              <Trash2 className="w-4 h-4" />
              <span>Löschen</span>
            </button>

            <div className="bg-white p-2 rounded shadow-sm">
              <p className="text-xs font-semibold text-gray-600 mb-1">Farbe</p>
              <div className="grid grid-cols-4 gap-1">
                {colors.map(c => (
                  <button key={c} onClick={() => setColor(c)} className={`w-8 h-8 rounded border ${color === c ? 'border-blue-500 ring-2 ring-blue-200' : 'border-gray-300'}`} style={{ backgroundColor: c }} />
                ))}
              </div>
            </div>
          </div>

          {/* Canvas */}
          <div className="flex-1 bg-gray-200 p-3 flex items-center justify-center overflow-auto">
            <canvas 
              ref={canvasRef}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
              className="shadow-2xl cursor-move"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-3 py-2 flex justify-between items-center border-t">
          <div className="flex items-center gap-3">
            <div className="text-sm text-gray-600">
              {selectedObject !== null ? 'Objekt ausgewählt - Ziehen zum Verschieben' : 'Klicken Sie ein Objekt zum Auswählen'}
            </div>
            {totalImages > 1 && (
              <div className="text-sm text-gray-500 border-l pl-3">
                Bild {(imageIndex || 0) + 1} von {totalImages}
              </div>
            )}
          </div>
          <div className="flex gap-2">
            {onPrevious && (
              <button onClick={onPrevious} className="px-4 py-1.5 text-sm border rounded hover:bg-gray-100">
                ← Zurück
              </button>
            )}
            <button onClick={onClose} className="px-4 py-1.5 text-sm border rounded hover:bg-gray-100">
              Abbrechen
            </button>
            <button onClick={handleSave} className="px-4 py-1.5 text-sm bg-green-500 text-white rounded hover:bg-green-600 flex items-center gap-1">
              <Save className="w-4 h-4" />
              Speichern
            </button>
            {onNext && (
              <button onClick={onNext} className="px-4 py-1.5 text-sm bg-blue-500 text-white rounded hover:bg-blue-600">
                Weiter →
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SimpleImageEditor;
